import { useState, useEffect, useCallback } from 'react';

import { RefreshTokenService } from '../services/refreshTokenService';
import { CookieManager } from '../utils/cookieManager';
import { TokenUtils } from '../utils/tokenUtils';

export interface RefreshTokenDebugInfo {
  cookies: {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasExpiry: boolean;
    expiryDate?: Date;
  };
  tokenValidation: {
    isValid: boolean;
    isExpired: boolean;
    timeRemaining: string;
    needsRefresh: boolean;
  };
  serviceStatus: {
    isRefreshing: boolean;
    retryCount: number;
    queueLength: number;
  };
  lastRefresh?: Date;
}

export const useRefreshTokenDebug = () => {
  const [debugInfo, setDebugInfo] = useState<RefreshTokenDebugInfo>({
    cookies: {
      hasAccessToken: false,
      hasRefreshToken: false,
      hasExpiry: false,
    },
    tokenValidation: {
      isValid: false,
      isExpired: true,
      timeRemaining: 'N/A',
      needsRefresh: true,
    },
    serviceStatus: {
      isRefreshing: false,
      retryCount: 0,
      queueLength: 0,
    },
  });

  const updateDebugInfo = useCallback(() => {
    const cookies = CookieManager.getAuthCookiesInfo();
    const accessToken = CookieManager.getAccessToken();

    let tokenValidation = {
      isValid: false,
      isExpired: true,
      timeRemaining: 'N/A',
      needsRefresh: true,
    };

    if (accessToken) {
      const validation = TokenUtils.validateToken(accessToken);
      tokenValidation = {
        isValid: validation.isValid,
        isExpired: validation.isExpired,
        timeRemaining: TokenUtils.getTokenTimeRemaining(accessToken),
        needsRefresh: TokenUtils.isTokenNearExpiry(accessToken, 300), // 5 minutos
      };
    }

    const serviceStatus = RefreshTokenService.getStatus();

    setDebugInfo({
      cookies,
      tokenValidation,
      serviceStatus,
    });
  }, []);

  const forceRefresh = useCallback(async () => {
    try {
      console.log('[useRefreshTokenDebug] Forçando refresh...');
      await RefreshTokenService.forceRefresh();
      updateDebugInfo();
    } catch (error) {
      console.error('[useRefreshTokenDebug] Erro ao forçar refresh:', error);
    }
  }, [updateDebugInfo]);

  const clearCookies = useCallback(() => {
    CookieManager.clearAllAuthCookies();
    updateDebugInfo();
  }, [updateDebugInfo]);

  const healthCheck = useCallback(async () => {
    try {
      const isHealthy = await RefreshTokenService.healthCheck();
      console.log('[useRefreshTokenDebug] Health check result:', isHealthy);
      return isHealthy;
    } catch (error) {
      console.error('[useRefreshTokenDebug] Health check failed:', error);
      return false;
    }
  }, []);

  // Atualizar informações a cada 5 segundos
  useEffect(() => {
    updateDebugInfo();

    const interval = setInterval(updateDebugInfo, 5000);

    return () => clearInterval(interval);
  }, [updateDebugInfo]);

  return {
    debugInfo,
    updateDebugInfo,
    forceRefresh,
    clearCookies,
    healthCheck,
  };
};
